import { useState, useEffect } from 'react';
import { api, endpoints } from '../config/api';
import { PageTitle } from '../components';
import { useConfig } from '../context/ConfigContext';
import { getAssetUrl } from '../config/assets';

function AgendaPage() {
  const { config } = useConfig();
  const mujerKvUrl = config?.mujerKvUrl || getAssetUrl('mujerKv');
  const logoMedicina = config?.welcomeLogos?.[0]?.url || getAssetUrl('logoMedicina');
  const logoHeel = config?.welcomeLogos?.[1]?.url || getAssetUrl('logoHeel');
  const [event, setEvent] = useState(null);
  const [agendas, setAgendas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeDay, setActiveDay] = useState(1);

  useEffect(() => {
    const load = async () => {
      try {
        const ev = await api.get(endpoints.eventActive());
        setEvent(ev);
        if (ev?.id) {
          const items = await api.get(endpoints.agendasByEvent(ev.id));
          setAgendas(Array.isArray(items) ? items : []);
        }
      } catch (err) {
        setError(err?.message || 'Error al cargar la agenda');
        setAgendas([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const agendasByDay = agendas.reduce((acc, item) => {
    const day = item.day ?? 1;
    if (!acc[day]) acc[day] = [];
    acc[day].push(item);
    return acc;
  }, {});

  const days = [...new Set(agendas.map((a) => a.day ?? 1))].sort((a, b) => a - b);
  const currentAgendas = agendasByDay[activeDay] || [];
  const getFechaTextoForDay = (d) => agendas.find((a) => (a.day ?? 1) === d)?.fechaTexto || `Día ${d}`;

  // Agrupar por fecha, hora y tema; concatenar speakers
  const groupedAgendas = currentAgendas.reduce((acc, item) => {
    const key = `${item.fechaTexto || getFechaTextoForDay(activeDay)}|${item.time || ''}|${item.title || ''}`;
    if (!acc[key]) {
      acc[key] = { fechaTexto: item.fechaTexto || getFechaTextoForDay(activeDay), time: item.time, title: item.title, description: item.description, speakers: [] };
    }
    if (item.speakerName) acc[key].speakers.push(item.speakerName);
    return acc;
  }, {});

  const timeToMinutes = (t) => {
    if (!t) return 0;
    const [h, m] = String(t).split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
  };

  const isBreak = (title) => /^\s*break\s*$/i.test(String(title || ''));

  const agendaItems = Object.values(groupedAgendas)
    .map((g) => ({
      ...g,
      speakersText: g.speakers.join(' - ') || '—',
      isBreak: isBreak(g.title),
    }))
    .sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Cargando agenda...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden pb-20">
      <div
        className="absolute inset-0 -z-10"
        style={{
          background: config?.bodyBackgroundImage
            ? `linear-gradient(180deg, rgba(15, 60, 50, 0.85) 0%, rgba(20, 80, 70, 0.75) 50%, rgba(25, 100, 90, 0.7) 100%), url(${config.bodyBackgroundImage}) center/cover`
            : 'linear-gradient(180deg, #0f3c32 0%, #145046 50%, #19645a 100%)',
        }}
      />

      <div className="relative z-0 flex flex-col flex-1 items-center justify-between px-4 pt-6">
        <div className="relative z-0 flex flex-col flex-col items-center justify-between px-4 pt-10">
          <div className="relative w-full overflow-hidden rounded-lg p-3 sm:p-4 flex items-center min-h-[180px] sm:min-h-[220px]">
            {config?.headerImage && (
              <div className="z-20 flex-1 min-w-0 pl-2 sm:pl-4 mr-12 sm:mr-20">
                <img src={config.headerImage} alt="37 Simposio" className="h-auto w-full max-w-[280px] sm:max-w-[347px] object-contain object-left" />
              </div>
            )}
            {mujerKvUrl && (
              <div className="absolute right-0 bottom-0 h-full w-1/3 sm:w-1/4 flex justify-center items-end pointer-events-none">
                <img src={mujerKvUrl} alt="" className="pb-6 sm:pb-10 h-[100%] sm:h-[110%] w-auto object-contain object-bottom mix-blend-screen brightness-110 opacity-40" />
              </div>
            )}
          </div>
        </div>
        {process.env.REACT_APP_AGENDA_URL ? (
          <a href={process.env.REACT_APP_AGENDA_URL} target="_blank" rel="noopener noreferrer" className="block w-full cursor-pointer hover:opacity-90 transition-opacity">
            <PageTitle>Agenda</PageTitle>
          </a>
        ) : (
          <PageTitle>Agenda</PageTitle>
        )}

        <div className="flex-1 w-full max-w-4xl mx-auto p-4 overflow-y-auto">
          <div className="p-4">
            {error && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-amber-800">{error}</p>
              </div>
            )}

            {days.length > 0 && (
              <div className="flex gap-2 mb-4">
                {days.map((day) => (
                  <button
                    key={day}
                    onClick={() => setActiveDay(day)}
                    className={`px-4 py-2 font-semibold rounded-lg transition-colors ${
                      activeDay === day
                        ? 'bg-[#CEF630] text-[#0f3c32] shadow-md'
                        : 'bg-white/20 text-white/80 hover:bg-white/30'
                    }`}
                  >
                    {getFechaTextoForDay(day)}
                  </button>
                ))}
              </div>
            )}

            {agendaItems.length === 0 && !error ? (
              <div className="p-6 text-center text-white/80 rounded-xl bg-[#0f3c32]/60 backdrop-blur-sm">
                No hay eventos en la agenda para este día.
              </div>
            ) : (
              <div className="rounded-xl overflow-hidden bg-[#0f3c32]/60 backdrop-blur-sm border border-white/10">
                <h3 className="text-center font-bold text-white py-4 text-lg">
                  Agenda {getFechaTextoForDay(activeDay)}
                </h3>
                <div className="overflow-x-auto">
                  <div className="min-w-full">
                    {/* Header */}
                    <div className="grid grid-cols-[70px_1fr_100px] sm:grid-cols-[80px_1fr_120px] gap-2 px-4 py-3 text-[#CEF630] font-semibold text-sm border-b border-white/20">
                      <div>Horario</div>
                      <div>Tema</div>
                      <div className="text-right">Speaker</div>
                    </div>
                    {/* Rows */}
                    {agendaItems.map((item, idx) =>
                      item.isBreak ? (
                        <div
                          key={idx}
                          className="grid grid-cols-[70px_1fr_0px] sm:grid-cols-[80px_1fr_120px] gap-2 px-4 py-3 items-start border-b border-white/10 last:border-0"
                        >
                          <div>
                        <span className="inline-block px-2 py-1 rounded-lg bg-[#CEF630] text-[#0f3c32] text-sm font-medium">
                          {item.time}
                        </span>
                      </div>
                      <div 
                          className="px-1 py-2 bg-[#CEF630] text-[#0f3c32] font-bold text-center rounded-[2rem]">{item.title}</div>
                        </div>
                      ) : (
                        <div
                          key={idx}
                          className="grid grid-cols-[70px_1fr_100px] sm:grid-cols-[80px_1fr_120px] gap-2 px-4 py-3 items-start border-b border-white/10 last:border-0"
                        >
                          <div>
                            <span className="inline-block px-2 py-1 rounded-lg bg-[#CEF630] text-[#0f3c32] text-sm font-medium">
                              {item.time}
                            </span>
                          </div>
                          <div className="text-white text-sm">
                            <div className="font-medium">{item.title}</div>
                            {item.description && String(item.description).trim() && (
                              <div className="text-white/75 text-xs mt-1">{item.description}</div>
                            )}
                          </div>
                          <div className="text-white text-sm text-right">
                            {item.speakersText}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="relative z-20 flex justify-between items-end px-4 py-6 pb-8 gap-4">
        <div className="flex flex-col items-start gap-2 min-w-0 flex-1">
          {logoMedicina && (
            <img src={logoMedicina} alt="Medicina Biorreguladora de Sistemas" className="h-[110%] w-auto sm:h-20 object-contain object-left max-w-[75%]" />
          )}
        </div>
        <div className="flex items-end flex-shrink-0">
          {logoHeel && (
            <img src={logoHeel} alt="Heel" className="h-8 sm:h-10 object-contain" />
          )}
        </div>
      </div>
    </div>
  );
}

export default AgendaPage;
